import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AuthUser } from '../types';
import * as authApi from '../api/authApi';

export interface AuthState {
  user: AuthUser | null;
  /** true tant que la session n'a pas été restaurée (au démarrage). */
  restoring: boolean;
  /** État de la connexion. */
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  restoring: true,
  status: 'idle',
  error: null,
};

/** Connexion : appelle l'API, persiste le token, mémorise l'utilisateur. */
export const login = createAsyncThunk<
  AuthUser,
  { email: string; password: string },
  { rejectValue: string }
>('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const { token, user } = await authApi.login(email, password);
    authApi.storeToken(token);
    return user;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Connexion impossible.');
  }
});

/** Restaure la session au démarrage si un token est persisté. */
export const restoreSession = createAsyncThunk<AuthUser | null, void, { rejectValue: string }>(
  'auth/restore',
  async () => {
    const token = authApi.getStoredToken();
    if (!token) return null;
    try {
      const { user } = await authApi.fetchCurrentUser();
      return user;
    } catch {
      // Token invalide/expiré : on le supprime et on repart déconnecté.
      authApi.clearStoredToken();
      return null;
    }
  },
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Déconnexion : supprime le token et l'utilisateur. */
    logout(state) {
      authApi.clearStoredToken();
      state.user = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Erreur inconnue';
      })
      .addCase(restoreSession.pending, (state) => {
        state.restoring = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.restoring = false;
        state.user = action.payload;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.restoring = false;
        state.user = null;
      });
  },
  selectors: {
    selectUser: (state) => state.user,
    selectIsAuthenticated: (state) => state.user !== null,
    selectIsRestoring: (state) => state.restoring,
    selectAuthStatus: (state) => state.status,
    selectAuthError: (state) => state.error,
  },
});

export const { logout } = authSlice.actions;
export const {
  selectUser,
  selectIsAuthenticated,
  selectIsRestoring,
  selectAuthStatus,
  selectAuthError,
} = authSlice.selectors;
